// Shared presentation helpers for the accident / incident register (Session AOM): the labels and
// badges for the four enums, the RIDDOR answer as a three-state badge, and two small pure helpers.
//
// THE RIDDOR BADGE HAS THREE STATES, NOT TWO, and that is the point of it. "Not assessed" is a real
// answer and is shown as such rather than as an absence: an assessor asking "have you had any
// RIDDOR reportable accidents" needs to know the difference between "no" and "nobody has looked".
import type {
  AccidentEventType,
  AccidentHospitalAdmission,
  AccidentRiddorCategory,
  AccidentStatus,
} from "../../lib/api";

export const EVENT_TYPE_LABEL: Record<AccidentEventType, string> = {
  employee_injury: "Injury to an employee",
  non_worker_injury: "Injury to a member of the public",
  dangerous_occurrence: "Dangerous occurrence",
  near_miss: "Near miss",
  occupational_disease: "Occupational disease",
  other: "Other",
};

export const STATUS_LABEL: Record<AccidentStatus, string> = {
  open: "Open",
  under_investigation: "Under investigation",
  reported_to_hse: "Reported to the HSE",
  closed: "Closed",
};

export const HOSPITAL_LABEL: Record<AccidentHospitalAdmission, string> = {
  none: "No hospital involvement",
  treated_discharged: "Taken to hospital, treated and discharged",
  admitted: "Admitted to hospital",
};

// Short forms for the register, where the full sentence would not fit a table cell.
export const HOSPITAL_SHORT: Record<AccidentHospitalAdmission, string> = {
  none: "No hospital",
  treated_discharged: "Treated and discharged",
  admitted: "Admitted",
};

// THE STATUS AND THE RIDDOR ANSWER ARE REGISTRY PILLS (design system Session 7a). The call sites
// render <StatusPill word={STATUS_LABEL[s]} tone={statusEntry("accident", s).tone} /> (this page's own
// word, the registry's tone) and <StatusPill domain="riddor" status={reportable-as-key} />, with
// riddorKey below turning the three-state boolean into the registry key. Reportable is red because it
// carries a legal duty with a clock on it, not because the accident was worse; not reportable is a
// settled, recorded decision and reads green; unassessed is amber, because it is the one state that
// needs somebody to act.
export function riddorKey(reportable: boolean | null): "unassessed" | "reportable" | "not_reportable" {
  if (reportable === null) return "unassessed";
  return reportable ? "reportable" : "not_reportable";
}

// The countdown to the written report, as a short phrase. Null when there is nothing to count down
// to (no determination, no stated window, or the report has already been made).
export function writtenReportCountdown(days: number | null): { text: string; urgent: boolean } | null {
  if (days === null) return null;
  if (days < 0) return { text: `Written report ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} overdue`, urgent: true };
  if (days === 0) return { text: "Written report due today", urgent: true };
  return { text: `Written report due in ${days} day${days === 1 ? "" : "s"}`, urgent: days <= 3 };
}

// An ISO instant as the value a <input type="datetime-local"> wants, in the viewer's own local time
// (which is London for this office). Returns "" for null, so the field renders empty rather than
// showing "Invalid Date".
export function toLocalInputValue(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// The same for a plain date field.
export function toDateInputValue(iso: string | null): string {
  return iso ? iso.slice(0, 10) : "";
}

export const EVENT_TYPES: AccidentEventType[] = [
  "employee_injury",
  "non_worker_injury",
  "dangerous_occurrence",
  "near_miss",
  "occupational_disease",
  "other",
];

export const HOSPITAL_OPTIONS: AccidentHospitalAdmission[] = ["none", "treated_discharged", "admitted"];

// Ordered for the office's own reading, not alphabetically: the categories that carry a
// without-delay duty first, then the rest, then the recorded "not reportable" decision last.
export const RIDDOR_CATEGORY_ORDER: AccidentRiddorCategory[] = [
  "death",
  "specified_injury",
  "dangerous_occurrence",
  "non_worker_hospital",
  "gas_incident",
  "over_7_day",
  "occupational_disease",
  "not_reportable",
];
